import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { CloseButton } from '@/styles/StandardButtons';
import { getApiUrl } from '@/lib/api-config';
import { generateIdempotencyKey } from '@/lib/idempotent-fetch';
import { supabase } from '@/lib/supabase';


interface ProjectSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Step {
  id: string;
  name: string;
}

const STEPS: Step[] = [
  { id: 'parsing', name: 'Parsing text / PDF scope' },
  { id: 'extracting', name: 'Extracting task data' },
  { id: 'client', name: 'Creating client' },
  { id: 'project', name: 'Creating project' },
  { id: 'review', name: 'Reviewing scope' },
  { id: 'change_orders', name: 'Creating change orders' },
  { id: 'tasks', name: 'Decomposing tasks' },
  { id: 'materials', name: 'Creating material orders' },
  { id: 'dependencies', name: 'Inferring dependencies' },
  { id: 'duration', name: 'Estimating duration' },
  { id: 'schedule', name: 'Creating schedule' },
  { id: 'purchase_orders', name: 'Creating purchase orders' },
  { id: 'budget', name: 'Calculating budget' },
  { id: 'checklists', name: 'Creating checklists' },
];

// Map orchestrator step names to UI step indices
const ORCHESTRATOR_STEP_MAP: { [key: string]: number } = {
  'pdf_parser': 0,
  'scope_normalization': 1,
  'client_creation': 2,
  'project_creation': 3,
  'scope_review': 4,
  'change_orders': 5,
  'task_decomposition': 6,
  'materials': 7,
  'dependencies_inference': 8,
  'duration_estimation': 9,
  'schedule_generation': 10,
  'purchase_orders': 11,
  'budget': 12,
  'checklists': 13,
};

export function ProjectSetupModal({ isOpen, onClose }: ProjectSetupModalProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [projectName, setProjectName] = useState<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(-1);
      setUploadedFile(null);
      setIsProcessing(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Process file with real orchestrator
  const processFile = async (file: File) => {
    try {
      setUploadedFile(file);
      setIsProcessing(true);
      setCurrentStepIndex(-1);
      setSummary(null);
      setProjectName('');

      // Get user session (the API call itself is authenticated via the
      // fetch interceptor, which attaches the session's Bearer token)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User session not found');
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Idempotency-Key: one per file upload attempt -- guards against a
      // double-submit or a multi-tab/device race duplicating a full
      // project setup (11+ LLM calls, real DB writes). No automatic retry
      // wrapper here (unlike bob-chat): this response is an SSE stream the
      // client consumes incrementally below, so a network drop mid-stream
      // can't be safely retried by just re-sending the same request --
      // that would need a reconnect-and-resume design, out of scope here.
      const response = await fetch(getApiUrl('api/project-setup'), {
        method: 'POST',
        headers: { 'Idempotency-Key': generateIdempotencyKey() },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the stream for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              // Handle progress updates
              if (data.step && data.status) {
                const stepIndex = ORCHESTRATOR_STEP_MAP[data.step];
                if (stepIndex !== undefined) {
                  // Show step as active when it starts, or move to next when completed/skipped
                  if (data.status === 'started') {
                    setCurrentStepIndex(stepIndex);
                  } else if (data.status === 'completed' || data.status === 'skipped') {
                    // Move to the next step (or stay on this one if it's the last)
                    setCurrentStepIndex(Math.min(stepIndex + 1, STEPS.length - 1));
                  } else if (data.status === 'failed') {
                    // Still show the step even if it failed
                    setCurrentStepIndex(stepIndex);
                  }
                }
              }
              
              // Handle completion
              if (data.type === 'complete') {
                setProjectName(data.project_name || 'New Project');
                setSummary(data.summary);
                setIsProcessing(false);
                return;
              }
              
              // Handle errors
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error processing file:', error);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsProcessing(false);
      setCurrentStepIndex(-1);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to setup project'}`);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Handle drag and drop
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  // Handle cancel
  const handleCancel = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsProcessing(false);
    setCurrentStepIndex(-1);
    setUploadedFile(null);
    onClose();
  };

  // Calculate progress
  const progress = currentStepIndex >= 0 
    ? ((currentStepIndex + 1) / STEPS.length) * 100 
    : 0;

  // Step height for consistent spacing
  const STEP_HEIGHT = 36;

  // Check if processing is complete
  const isComplete = summary !== null && !isProcessing;

  if (!isOpen) return null;

  const showUpload = !uploadedFile;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-50"
        onClick={handleCancel}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white rounded-2xl border border-gray-200/50 shadow-2xl">
        {/* Close Button */}
        <CloseButton onClick={handleCancel} className="absolute right-4 top-4 z-10" />

        {/* Title */}
        <div className="px-8 pt-8 pb-4">
          <h2 className="text-base font-medium text-gray-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            KrewUp AI
          </h2>
        </div>

        {/* Body - Fixed height container */}
        <div className="px-8 pb-8 min-h-[280px] flex flex-col">
          {/* File Upload Area - always visible when no file uploaded */}
          {showUpload && (
            <div
              className="flex-1 flex items-center justify-center text-center transition-all duration-200 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                type="file"
                id="scope-upload"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <label
                htmlFor="scope-upload"
                className="cursor-pointer text-gray-600 hover:text-gray-900 transition-colors py-12 px-8 w-full text-base"
              >
                Drop scope here to setup your project
              </label>
            </div>
          )}

          {/* Progress Section */}
          {isProcessing && currentStepIndex >= 0 && !isComplete && (
            <div className="flex-1 flex flex-col space-y-6">
              {/* Progress Bar - on top, slightly lower */}
              <div className="space-y-2 pr-12 pt-2">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Processing</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="relative w-full h-1 bg-gray-200 overflow-hidden rounded-full">
                  <div 
                    className="absolute top-0 left-0 h-full transition-all duration-500 ease-out rounded-full progress-bar-fill"
                    style={{ 
                      width: `${progress}%`,
                    }}
                  />
                  <style>{`
                    @keyframes progressShimmer {
                      0% {
                        background-position: 200% 0;
                      }
                      100% {
                        background-position: -200% 0;
                      }
                    }
                    .progress-bar-fill {
                      background: linear-gradient(
                        90deg,
                        ${colors.buttonSecondary} 0%,
                        ${colors.buttonSecondary} 40%,
                        rgb(var(--white) / 0.6) 50%,
                        ${colors.buttonSecondary} 60%,
                        ${colors.buttonSecondary} 100%
                      );
                      background-size: 200% 100%;
                      animation: progressShimmer 1.92s linear infinite;
                    }
                  `}</style>
                </div>
              </div>

              {/* Step Carousel - Track-based */}
              <div className="relative h-[180px] overflow-hidden">
                <div
                  className="absolute left-0 w-full transition-transform duration-700 ease-[cubic-bezier(.4,0,.2,1)]"
                  style={{
                    transform: `translateY(${
                      90 - currentStepIndex * STEP_HEIGHT
                    }px)`,
                  }}
                >
                  {STEPS.map((step, index) => {
                    const distance = Math.abs(index - currentStepIndex);
                    const isActive = index === currentStepIndex;

                    return (
                      <div
                        key={step.id}
                        className={cn(
                          "h-[36px] flex items-center justify-center text-center transition-all duration-500",
                          isActive
                            ? "text-gray-900 text-base font-medium"
                            : "text-gray-400 text-sm"
                        )}
                        style={{
                          opacity: Math.max(1 - distance * 0.4, 0.2),
                          transform: `scale(${isActive ? 1 : 0.92})`,
                          filter: isActive ? "none" : "blur(0.2px)",
                        }}
                      >
                        {step.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Completion Summary */}
          {isComplete && summary && (
            <div className="flex-1 flex flex-col space-y-4">
              <div className="text-center py-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Project setup completed!
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {projectName || 'New Project'}
                </p>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="font-medium text-gray-900 mb-2">Created:</div>
                <div className="space-y-1 text-gray-700 pl-4">
                  <div>- {summary.tasks} tasks</div>
                  <div>- {summary.schedule_items} schedule items</div>
                  <div>- {summary.checklists} checklists</div>
                  <div>- {summary.material_orders} material orders</div>
                  <div>- {summary.purchase_orders} purchase orders</div>
                  {summary.budget && <div>- Budget created</div>}
                </div>
                
                <div className="pt-2 space-y-1 text-gray-600 text-xs">
                  <div>Assumptions: {summary.assumptions}</div>
                  <div>Errors: {summary.errors}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(isProcessing || isComplete) && (
          <div className="flex justify-end px-8 pb-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              {isComplete ? 'Close' : 'Cancel'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

